import { Menu, Layout } from "antd";
import "./index.scss";
import { useEffect, useState, useCallback } from "react";

const { Sider } = Layout;

export default function Sidebar(props) {
  const {
    setBreadcrumbName,
    setBreadcrumb,
    setLoading,
    apiName,
    setApiName,
    selectName, // 搜索输入的接口名称
    menuList,
  } = props;
  const [selectedKeys, setSelectedKeys] = useState([]); // 初始化选中的菜单项，将在useEffect中设置
  const [openKeys, setOpenKeys] = useState([]); // 初始化展开的菜单项，将在useEffect中设置
  
  // 生成唯一key的辅助函数，与initMenu中的逻辑保持一致
  const generateUniqueKey = (item, parentKey = "", index = 0) => {
    const breadcrumbPart = item.breadcrumb || item.menu_name;
    
    if (item.api_id) {
      return parentKey 
        ? `${parentKey}-${breadcrumbPart}-${item.api_id.trim()}`
        : `${breadcrumbPart}-${item.api_id.trim()}`;
    } else {
      return parentKey 
        ? `${parentKey}-${breadcrumbPart}-${item.menu_name}-${index}`
        : `${breadcrumbPart}-${item.menu_name}-${index}`;
    }
  };
  
  // 递归查找菜单项并生成对应的key
  const findMenuItemKey = useCallback((list, targetName, targetApiId, parentKey = "", parentIndex = 0) => {
    for (let index = 0; index < list.length; index++) {
      const item = list[index];
      const currentKey = generateUniqueKey(item, parentKey, index);
      
      // 检查是否匹配
      if ((item.menu_name === targetName || item.api_id === targetName) || 
          (targetApiId && item.api_id === targetApiId)) {
        return currentKey;
      }
      
      // 如果有子项，递归查找
      if (item.children && item.children.length > 0) {
        const foundKey = findMenuItemKey(item.children, targetName, targetApiId, currentKey, index);
        if (foundKey) {
          return foundKey;
        }
      }
    }
    return null;
  }, []);
  
  const initMenu = (list, parentKey = "") => {
    list = list?.map((item, index) => {
      // 使用辅助函数生成唯一key
      const uniqueKey = generateUniqueKey(item, parentKey, index);
      
      if (item.children && item.children.length > 0) {
        return (
          <Menu.SubMenu title={item.menu_name} key={uniqueKey}>
            {initMenu(item.children, uniqueKey)}
          </Menu.SubMenu>
        );
      }

      const isAlphaNumeric = /^[a-z0-9]+$/.test(selectName);
      // if (!isAlphaNumeric) console.log(selectName);
      const renderMenuName = (menuName) => {
        if (selectName) {
          // 转义 selectName 中的正则特殊字符
          const escapedSelectName = selectName.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
          );
          const parts = menuName.split(
            new RegExp(`(${escapedSelectName})`, "gi")
          );

          return parts.map((part, idx) =>
            part.toLowerCase() === selectName.toLowerCase() ? (
              <span key={idx} style={{ color: "#fe545f", fontWeight: "bold" }}>
                {part}
              </span>
            ) : (
              part
            )
          );
        }
        return menuName;
      };

      return (
        <Menu.Item
          key={uniqueKey}
          onClick={() => {
            setBreadcrumbName(item.menu_name);
            setBreadcrumb(item);
            setLoading(true);
            setSelectedKeys([uniqueKey]); // 使用uniqueKey而不是menu_name来匹配高亮
            setApiName(null);
          }}
        >
          <span>{renderMenuName(item.menu_name)}</span>
        </Menu.Item>
      );
    });
    return list;
  };
  
  // 添加逻辑：查找匹配的菜单项并触发点击事件
  const handleClickMenu = useCallback((menuName, flag) => {
    const result = props.routerList[1]?.children?.reduce((acc, item) => {
      if (item.children && item.children.length > 0) {
        acc.push(...item.children);
      }
      return acc;
    }, []);
    const matchedItem = result.find(
      (item) => item.menu_name === menuName || item.api_id === menuName
    );

    if (matchedItem) {
      setBreadcrumbName(matchedItem.menu_name);
      setBreadcrumb(matchedItem);
      setLoading(true);
      
      // 使用递归查找函数找到对应的key
      const menuKey = findMenuItemKey(
        props.routerList?.filter((item) => !item.hide) || [],
        matchedItem.menu_name,
        matchedItem.api_id
      );
      
      if (menuKey) {
        setSelectedKeys([menuKey]);
      }
      
      // 设置展开的菜单项
      if (flag) {
        // 找到API参考的key
        const apiReferenceKey = findMenuItemKey(
          props.routerList?.filter((item) => !item.hide) || [],
          "API参考"
        );
        if (apiReferenceKey) {
          setOpenKeys([apiReferenceKey]);
        }
      } else {
        // 找到API参考和分类的key
        const apiReferenceKey = findMenuItemKey(
          props.routerList?.filter((item) => !item.hide) || [],
          "API参考"
        );
        const categoryKey = findMenuItemKey(
          props.routerList?.filter((item) => !item.hide) || [],
          matchedItem.breadcrumb
        );
        const keys = [];
        if (apiReferenceKey) keys.push(apiReferenceKey);
        if (categoryKey) keys.push(categoryKey);
        setOpenKeys(keys);
      }
    }
  }, [props.routerList, findMenuItemKey, setBreadcrumbName, setBreadcrumb, setLoading]);

  // 初始化菜单的选中和展开状态
  useEffect(() => {
    if (props.routerList && props.routerList.length > 0) {
      // 初始化"开通服务"的选中状态
      const openServiceKey = findMenuItemKey(
        props.routerList?.filter((item) => !item.hide) || [],
        "开通服务"
      );
      if (openServiceKey) {
        setSelectedKeys([openServiceKey]);
      }
      
      // 初始化"快速入门"的展开状态
      const quickStartKey = findMenuItemKey(
        props.routerList?.filter((item) => !item.hide) || [],
        "快速入门"
      );
      if (quickStartKey) {
        setOpenKeys([quickStartKey]);
      }
    }
  }, [props.routerList, findMenuItemKey]);
  
  // 在组件挂载时触发点击事件
  const updateselectName = useCallback(() => {
    if (selectName) {
      const flag = menuList?.find(
        (v) => v.menu_name === selectName || v.api_id === selectName
      );

      if (menuList?.length !== 0) {
        if (flag) {
          handleClickMenu(selectName);
        } else {
          handleClickMenu(menuList[0]?.menu_name, true);
        }
      } else {
        setBreadcrumbName("开通服务");
        setLoading(true);
        // 使用正确的key格式
        const openServiceKey = findMenuItemKey(
          props.routerList?.filter((item) => !item.hide) || [],
          "开通服务"
        );
        if (openServiceKey) {
          setSelectedKeys([openServiceKey]);
        }
        const apiReferenceKey = findMenuItemKey(
          props.routerList?.filter((item) => !item.hide) || [],
          "API参考"
        );
        if (apiReferenceKey) {
          setOpenKeys([apiReferenceKey]);
        }
      }
    }
  }, [selectName, menuList, props.routerList, findMenuItemKey, handleClickMenu, setBreadcrumbName, setLoading]);

  useEffect(() => {
    updateselectName();
  }, [updateselectName]);

  return (
    <div className="mySidebarContent">
      <Sider
        trigger={null}
        width={275}
        style={{
          height: "calc(100% - 40px)",
          background: "#fff",
        }}
        className="site-layout-background"
      >
        <div
          className="title"
          style={{
            background: "linear-gradient(223deg, #006bb3 0%, #15d2d6 100%)",
          }}
        >
          数据服务
        </div>

        <Menu
          mode="inline"
          style={{
            height: "100%",
            borderRight: 0,
            overflow: "auto",
            background: "#fff",
          }}
          selectedKeys={selectedKeys}
          openKeys={openKeys} // 设置展开的菜单项
          onOpenChange={(keys) => setOpenKeys(keys)} // 处理展开菜单项的变化
        >
          {initMenu(
            props.routerList?.filter((item) => !item.hide),
            false
          )}
        </Menu>
      </Sider>
    </div>
  );
}
